import { useParams } from '@tanstack/react-router';
import { Ban, Mail, RotateCcw, Scale } from 'lucide-react';
import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { FeatureErrorBoundary, useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import { FormLayout, FormSection } from '@nucleus-commerce/admin-kit/forms';
import { PageContent, PageHeader } from '@nucleus-commerce/admin-kit/layout';
import { Can, usePermissions } from '@nucleus-commerce/admin-kit/permissions';
import { useFormat } from '@nucleus-commerce/i18n/react';
import { Button } from '@nucleus-commerce/ui/components/button';
import { Progress } from '@nucleus-commerce/ui/components/progress';
import { toast } from '@nucleus-commerce/ui/components/sonner';

import { useStore } from '@/shared/store/store-context';

import { AdjustBalanceDialog } from '../components/adjust-balance-dialog';
import { CustomerLink } from '../components/customer-link';
import { GiftCardEditCard } from '../components/gift-card-edit-card';
import { GiftCardStatusBadge } from '../components/gift-card-status-badge';
import { GiftCardTransactionsCard } from '../components/gift-card-transactions-card';
import { ReassignCustomerDialog } from '../components/reassign-customer-dialog';
import { SendGiftCardDialog } from '../components/send-gift-card-dialog';
import { useGiftCard } from '../hooks/use-gift-card';
import { useUpdateGiftCard } from '../hooks/use-gift-card-mutations';
import { sendBlocker, statusAction } from '../schemas/gift-card-rules';

const ROUTE = '/_authed/_active/s/$storeId/gift-cards/$giftCardId' as const;

function GiftCardDetail() {
  const { t } = useTranslation('giftCards');
  const format = useFormat();
  const store = useStore();
  const { can } = usePermissions();
  const message = useErrorMessage();
  const { giftCardId } = useParams({ from: ROUTE });
  const blockerId = useId();
  const [dialog, setDialog] = useState<'adjust' | 'send' | 'reassign' | null>(null);
  // Prefetched by the route loader, so this resolves from cache.
  const { data: card } = useGiftCard(store.id, giftCardId);
  const action = card ? statusAction(card) : 'disable';
  const setStatus = useUpdateGiftCard(
    store.id,
    action === 'disable' ? 'giftCards:toasts.disabled' : 'giftCards:toasts.enabled',
  );
  if (!card) return null;

  const blocker = sendBlocker(card);
  const closeDialog = (open: boolean) => {
    if (!open) setDialog(null);
  };
  const remainingShare =
    card.initial.amount > 0 ? Math.round((card.balance.amount / card.initial.amount) * 100) : 0;

  return (
    <PageContent>
      <PageHeader
        title={t('masked', { last4: card.last4 })}
        meta={<GiftCardStatusBadge status={card.status} />}
        description={
          <span title={format.utc(card.createdAt)}>
            {t('issuedAt', { when: format.relative(card.createdAt) })}
          </span>
        }
        backTo={{ to: '/s/$storeId/gift-cards', params: { storeId: store.id } }}
        backLabel={t('title')}
        actions={
          <Can permission="gift_cards.write">
            <Button
              variant="outline"
              disabled={blocker !== null}
              aria-describedby={blocker ? blockerId : undefined}
              onClick={() => setDialog('send')}
            >
              <Mail aria-hidden />
              {t('send.action')}
            </Button>
            <Button
              variant="outline"
              onClick={() => setDialog('adjust')}
              data-tour="gift-cards.detail.adjust"
            >
              <Scale aria-hidden />
              {t('adjust.action')}
            </Button>
            <Button
              variant="outline"
              disabled={setStatus.isPending}
              data-tour="gift-cards.detail.toggle-status"
              onClick={() =>
                setStatus.mutate(
                  {
                    id: card.id,
                    body: { status: action === 'disable' ? 'disabled' : 'active' },
                  },
                  { onError: (error) => toast.error(message(error)) },
                )
              }
            >
              {action === 'disable' ? <Ban aria-hidden /> : <RotateCcw aria-hidden />}
              {t(`actions.${action}`)}
            </Button>
          </Can>
        }
      />
      <FormLayout
        aside={
          <GiftCardEditCard
            key={card.updatedAt}
            storeId={store.id}
            card={card}
            readOnly={!can('gift_cards.write')}
          />
        }
      >
        <FormSection title={t('balance.title')} tour="gift-cards.detail.balance">
          <div className="flex items-baseline gap-2" data-testid="gift-card-balance">
            <span className="text-2xl font-semibold tabular-nums">
              {format.money(card.balance)}
            </span>
            <span className="text-muted-foreground text-sm">
              {t('balance.of', { initial: format.money(card.initial) })}
            </span>
          </div>
          <Progress value={remainingShare} aria-label={t('balance.remaining')} />
          <dl className="grid gap-2 text-sm sm:grid-cols-2">
            <div>
              <dt className="text-muted-foreground">{t('columns.customer')}</dt>
              <dd className="flex flex-wrap items-center gap-x-2">
                <CustomerLink storeId={store.id} customerId={card.customerId} />
                {can('gift_cards.write') && can('customers.read') ? (
                  <Button
                    type="button"
                    variant="link"
                    size="sm"
                    className="h-auto p-0"
                    onClick={() => setDialog('reassign')}
                  >
                    {card.customerId ? t('reassign.change') : t('reassign.attach')}
                  </Button>
                ) : null}
              </dd>
            </div>
            <div>
              <dt className="text-muted-foreground">{t('columns.expires')}</dt>
              <dd>
                {card.expiresAt ? (
                  <span title={format.utc(card.expiresAt)}>{format.date(card.expiresAt)}</span>
                ) : (
                  t('noExpiry')
                )}
              </dd>
            </div>
          </dl>
          {card.status === 'disabled' ? (
            <p className="text-muted-foreground text-sm">{t('balance.disabledHint')}</p>
          ) : null}
          {blocker && can('gift_cards.write') ? (
            <p id={blockerId} className="text-muted-foreground text-sm">
              {t(`send.blocked.${blocker}`)}
            </p>
          ) : null}
        </FormSection>
        <GiftCardTransactionsCard storeId={store.id} cardId={card.id} />
      </FormLayout>
      <Can permission="gift_cards.write">
        <AdjustBalanceDialog
          storeId={store.id}
          card={card}
          open={dialog === 'adjust'}
          onOpenChange={closeDialog}
        />
        <SendGiftCardDialog
          storeId={store.id}
          card={card}
          open={dialog === 'send'}
          onOpenChange={closeDialog}
        />
        <ReassignCustomerDialog
          storeId={store.id}
          card={card}
          open={dialog === 'reassign'}
          onOpenChange={closeDialog}
        />
      </Can>
    </PageContent>
  );
}

export function GiftCardDetailPage() {
  return (
    <FeatureErrorBoundary name="giftCards.detail">
      <GiftCardDetail />
    </FeatureErrorBoundary>
  );
}
